// This generates the book HTML

import * as _ from "underscore";

import spannify from "./spannify.js";

/**
 * If this is updated, then spannify.js needs to change.
 *
 * Before all versions before 0.6, books will be regenerated using the 0.6 algo, so as not to
 * mess up the positions (in UserBook and YouTube audio sync and Reader Review).
 * For version 0.7, books will be regenerated using the 0.7 algo (includes punctuation).
 */
export const htmlVersion = 0.7;

const htmlCharacters = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;", //,
  //    '"': '&quot;',
  //    "'": '&#39;'
};

/**
 * This is added as the first top-level section name if the text happens to start with a
 * second-level section header.
 */
export const dummyFirstSectionName = "Entire book";

export const escapeHTML = (input) =>
  input.replace(/[&<>]/g, (c) => htmlCharacters[c]);

export const categories = [
  "Fiction",
  "Non-Fiction",
  "Song",
  "Conversation",
  "Other",
];

/**
 * The category stored as "Conversation" is presented to users as "Dialogue".
 * The stored name is kept for backwards compatibility with existing books.
 */
export const dialogueCategory = "Conversation";

/** The name to show users for a stored category. */
export const categoryLabel = (category) =>
  category === dialogueCategory ? "Dialogue" : category;

/**
 * Strip standalone `---` horizontal-rule lines from text.
 *
 * Readlang's reader doesn't render markdown horizontal rules — they'd appear
 * as literal "---" to the reader. Setext-style `Heading\n---` underlines
 * (single newline before the dashes) are preserved since the reader renders
 * those as `<h2>`.
 *
 * @param {string} text
 * @returns {string}
 */
export const stripSeparators = (text) =>
  text.replace(/(\r?\n){2,}-{3,}[ \t]*(\r?\n){2,}/g, "\n\n");

/**
 * Normalizes markdown heading levels when they are excessively deep.
 * Only adjusts if the top-most heading level is ### or deeper, shifting
 * everything down by one level (e.g. ### → ##, #### → ###).
 * @param {string} text - Plain text with markdown headings
 * @returns {string}
 */
export const normalizeHeadingLevels = (text) => {
  const headingRegex = /(?:^|\r?\n)(#{1,6}) /g;
  let minLevel = Infinity;
  let match;
  while ((match = headingRegex.exec(text)) !== null) {
    minLevel = Math.min(minLevel, match[1].length);
  }

  if (minLevel < 3) {
    return text; // No headings, or already at # or ## — leave as-is
  }

  // Shift by one level only (e.g. ### → ##, #### → ###)
  return text.replace(/(?:^|\r?\n)(#{1,6}) /g, (fullMatch, hashes) => {
    const newHashes = "#".repeat(hashes.length - 1);
    return fullMatch.replace(hashes + " ", newHashes + " ");
  });
};

export const textToHtml = (plainText, options) => {
  let html = escapeHTML(plainText);

  html = html
    //.replace(/!\[(.+?)\]\((.+?)\)/g, '</p><img src="$2" alt="$1" /><p>')

    .replace(/\u00AD/g, "")

    // consolidate adjacent italic strings
    .replace(/([^_]_[^_\r\n]+)__([^_\r\n]+_[^_])/g, "$1$2")

    .replace(/(^|\r|\n)(.+?)(\r|\n)=+(\r|\n|$)/g, "</p><h1>$2</h1><p>")
    .replace(/(^|\r|\n)# (.+?)(\r|\n|$)/g, "</p><h1>$2</h1><p>")
    .replace(/(^|\r|\n)(.+?)(\r|\n)-+(\r|\n|$)/g, "</p><h2>$2</h2><p>")
    .replace(/(^|\r|\n)## (.+?)(\r|\n|$)/g, "</p><h2>$2</h2><p>")
    .replace(/(^|\r|\n)### (.+?)(\r|\n|$)/g, "</p><h3>$2</h3><p>")

    .replace(/__([^_\r\n]+?)__/g, "<strong>$1</strong>")
    .replace(/\*\*([^*\r\n]+?)\*\*/g, "<strong>$1</strong>")

    .replace(/_([^_\r\n]+?)_/g, "<i>$1</i>")
    .replace(/\*([^*\r\n]+?)\*/g, "<i>$1</i>")

    // Retain superscript HTML which has now been escaped
    .replace(/\^([^<\r\n]+?)\^/g, "<sup>$1</sup>")

    .replace(/(\r\n|\n\r|\n|\r){2,}/g, "</p><p>")

    // Remove empty newlines from start of paragraphs
    .replace(/<p>(\s+)?/g, "<p>")

    // Unescape exclamation marks:
    .replace(/\\!/g, "!");

  if (options.useEveryNewLine) {
    html = html.replace(/\r\n|\n\r|\n|\r/g, "<br>");
  } else {
    html = html.replace(/\r\n|\n\r|\n|\r/g, " ");
  }
  html = "<p>" + html + "</p>";

  // Remove empty paragraphs from start and end of text
  if (html.startsWith("<p></p>")) {
    html = html.substring(7);
  }
  if (html.endsWith("<p></p>")) {
    html = html.substring(0, html.length - 7);
  }

  return html;
};

const htmlTags = /<\/?[a-z]+[^<]*?>/g;
const stripHtmlTags = (input) => input.replace(htmlTags, "");

const createTableOfContents = (html) => {
  // create table of contents from <h1> and <h2> tags
  const header1Regexp = /<h1>(.*?)<\/h1>/g;
  const header2Regexp = /<h2>(.*?)<\/h2>/g;
  const spanRegexp = /<span data-p=([0-9]+)>/;
  let match;
  const sections = [];
  let firstSpan;
  let spanIndex;

  // Find <h1> tags
  while ((match = header1Regexp.exec(html)) !== null) {
    // find first span...
    firstSpan = spanRegexp.exec(match[1]);
    if (firstSpan) {
      spanIndex = parseInt(firstSpan[1], 10);
      sections.push({
        name: stripHtmlTags(match[1]),
        start: spanIndex,
        children: [],
      });
    }
  }
  let currentSection = 0;
  // Find <h2> tags
  while ((match = header2Regexp.exec(html)) !== null) {
    // find first span...
    firstSpan = spanRegexp.exec(match[1]);
    if (firstSpan) {
      spanIndex = parseInt(firstSpan[1], 10);

      while (
        sections[currentSection + 1] &&
        spanIndex > sections[currentSection + 1].start
      ) {
        currentSection++;
      }
      if (currentSection === 0 && sections.length === 0) {
        // TODO: always add this and give it the book title!!
        sections.push({
          name: dummyFirstSectionName,
          start: 0,
          children: [],
        });
      }
      sections[currentSection].children.push({
        name: stripHtmlTags(match[1]),
        start: spanIndex,
      });
    }
  }
  return sections;
};

export const needToGenerateHtml = (book) => {
  return !book.generatedVersion || book.generatedVersion < 0.6;
};

/**
 * Converts a book's plain text into the reader's spannified HTML, without
 * persisting anything. Used by generateHtml, and stand-alone to preview the
 * word list a text replacement would produce (e.g. to validate audio
 * alignment) before committing a destructive replacement.
 *
 * Note: normalizes a string `book.useEveryNewLine` to a boolean in place,
 * just as generateHtml always has.
 *
 * @param {any} book
 * @param {string} plainText
 * @param {{
 *   startPosition?: number,
 *   onEachSpan?: (position: number, offset: number, cumulativeOffset: number) => void,
 *   includePunctuation?: boolean,
 *   ignoreStrong?: boolean,
 * }} [extraOptions] - Overrides for the spannify options; without them the
 *   options match (re)generating the book at the latest html version.
 *
 * @returns {{html: string; totalSpans: number}}
 */
export const spannifyBookText = (book, plainText, extraOptions) => {
  if (typeof book.useEveryNewLine === "string") {
    book.useEveryNewLine = JSON.parse(book.useEveryNewLine);
  }

  const html = textToHtml(plainText, {
    htmlMarkup: book.htmlMarkup,
    useEveryNewLine: book.useEveryNewLine,
  });

  const spannifyOptions = {
    includePunctuation: true,
    ...extraOptions,
    language: book.language,
    ...(book.htmlMarkup ? { ignoreStrong: true } : {}),
  };
  return spannify(html, spannifyOptions);
};

// The callback arguments are (error, book, changed)
/**
 * @param {{
 *   callback: (error: any, book: any, changed: boolean, html?: string) => void;
 *   dataStore: any;
 *   forceRegenerate?: boolean;
 *   wordsPerChunk?: number;
 *   language?: string;
 *   ignoreStrong?: boolean;
 *   includePunctuation?: boolean;
 *   startPosition?: number;
 *   onEachSpan?: (position: number, offset: number, cumulativeOffset: number) => void;
 * }} options
 */
export const generateHtml = async (book, plainText, options) => {
  _.defaults(options, {
    wordsPerChunk: 1024,
  });

  /** @type {number} */
  let thisHtmlVersion;

  // Books from 2024-06-07 18:30:00 onwards which have generatedVersion=0.6 should
  // actually have generatedVersion=0.7 since they used the newer algo.
  if (
    book.generatedVersion === 0.6 &&
    book._id.getTimestamp().getTime() >= new Date(2024, 5, 7, 18, 30).getTime()
  ) {
    book.generatedVersion = 0.7;
  }

  if (!options.forceRegenerate && !needToGenerateHtml(book)) {
    options.callback(null, book, false);
    return;
  }

  // Let's (re)generate the HTML
  thisHtmlVersion =
    book.generatedVersion === undefined
      ? htmlVersion
      : book.generatedVersion >= 0.7
        ? book.generatedVersion
        : 0.6;
  delete book.htmlChunks;
  delete book.totalWords;
  delete book.generatedText1;
  delete book.generatedText2;

  let thisChunkFirstPosition = 0;
  let thisChunkFirstCharacter = 0;
  const chunks = [
    {
      firstCharacter: thisChunkFirstCharacter,
      firstPosition: thisChunkFirstPosition,
      positionIndexes: [],
    },
  ];
  const chunkSplitPoints = [0];

  options.onEachSpan = (position, offset, cumulativeOffset) => {
    const thisCharacterIndex = offset + cumulativeOffset;
    const chunkIndex = Math.floor(position / options.wordsPerChunk);

    if (chunks.length - 1 < chunkIndex) {
      chunks[chunks.length - 1].lastPosition = position - 1;
      thisChunkFirstPosition = position;
      thisChunkFirstCharacter = thisCharacterIndex;
      chunks.push({
        firstCharacter: thisChunkFirstCharacter,
        firstPosition: thisChunkFirstPosition,
        positionIndexes: [],
      });
      chunkSplitPoints.push(thisCharacterIndex);
    }

    const relativePosition = position - thisChunkFirstPosition;
    if (relativePosition > 0 && relativePosition % 8 === 0) {
      chunks[chunkIndex].positionIndexes[Math.floor(relativePosition / 8) - 1] =
        thisCharacterIndex - thisChunkFirstCharacter;
    }
  };
  options.includePunctuation = thisHtmlVersion >= 0.7;

  const spannified = spannifyBookText(book, plainText, options);

  if (book.imageURL) {
    spannified.html = '<img src="' + book.imageURL + '"/>' + spannified.html;
  }

  book.totalWords = spannified.totalSpans;
  chunks[chunks.length - 1].lastPosition = book.totalWords - 1;
  chunkSplitPoints.push(spannified.html.length);

  book.sections = createTableOfContents(spannified.html);

  // split the html into chunks
  try {
    // Process chunks with limited concurrency (10 at a time)
    const chunkData = chunks.map((chunk, index) => ({ chunk, index }));
    for (let batchStart = 0; batchStart < chunkData.length; batchStart += 10) {
      const batch = chunkData.slice(batchStart, batchStart + 10);
      await Promise.all(
        batch.map(async ({ chunk, index }) => {
          const chunkHtml = spannified.html.substring(
            chunkSplitPoints[index],
            chunkSplitPoints[index + 1]
          );
          const sha1 = await options.dataStore.addChunk(chunkHtml);
          chunk.sha1 = sha1;
        })
      );
    }
    book.htmlChunks = chunks;
    book.generatedVersion = thisHtmlVersion;
    options.callback(null, book, true, spannified.html);
  } catch (err) {
    console.error(err);
    options.callback(err, book, false);
  }
};

// Long enough for a typical attribution, short enough that pasting the text
// itself into the Author field can't swamp the library card.
export const maxAuthorLength = 100;

// YouTube-backed texts above this word count are unsupported: the reader
// hides the YouTube pane and the API refuses to attach more text or video.
export const maxYouTubeWords = 20000;

export const maxYouTubeWordsMessage =
  "Cannot add more than 20,000 words to a text with a YouTube video attached";

// TODO: do this more accurately for each language
const apostropheSplitLanguages = ["ca"];

/**
 * Tokenizes plain text into the words counted by word-count metrics. This is
 * the single word definition shared by readability metrics
 * (Book.getReadabilityMetrics → metrics.words) and the YouTube word-limit
 * guards, so the server's accept/reject decisions and the reader's
 * `allowYouTube` flag (metrics.words vs maxYouTubeWords) can never disagree
 * about a text's length.
 *
 * Words are maximal runs of Unicode letters and combining marks; apostrophes
 * count as word characters (so "don't" is one word) except in languages
 * where an apostrophe joins separate words (e.g. Catalan "l'home").
 *
 * @param {string} text
 * @param {string} [language]
 *
 * @returns {string[]}
 */
export const tokenizeWords = (text, language) => {
  const apostrophes =
    language && apostropheSplitLanguages.includes(language) ? "" : "'`";
  const wordRegExp = new RegExp("[^" + apostrophes + "\\p{L}\\p{M}]", "u");
  return text.split(wordRegExp).filter((word) => word.length > 0);
};

/**
 * @param {string} plainText
 * @param {string} [language]
 *
 * @returns {number}
 */
export const countWords = (plainText, language) =>
  tokenizeWords(plainText, language).length;

/**
 * The single length check for pairing a text with a YouTube video. Every
 * guard — book creation, text replacement, addTimings, and attaching a video
 * to an existing book — must use this so the same text can't pass one path
 * and fail another.
 *
 * @param {string} plainText
 * @param {string} [language]
 *
 * @returns {boolean}
 */
export const exceedsYouTubeWordLimit = (plainText, language) =>
  countWords(plainText, language) > maxYouTubeWords;

/** Reverses `escapeHTML`. */
export const unescapeHTML = (input) =>
  input.replace(
    /&(amp|lt|gt);/g,
    (entity) => ({ "&amp;": "&", "&lt;": "<", "&gt;": ">" })[entity] ?? entity
  );

/**
 * Converts a stretch of a book's generated HTML to plain text: block-level
 * boundaries become newlines, other tags are stripped.
 *
 * @param {string} html
 * @returns {string}
 */
export const htmlToPlainText = (html) =>
  unescapeHTML(
    html
      .replace(/<\/?(p|h1|h2|h3|br)\b[^>]*>/g, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/\n{3,}/g, "\n\n")
  );

/**
 * The plain text of a book's words from `fromPosition` to `toPosition`
 * inclusive, taken from its generated HTML (`<span data-p=N>word</span>`).
 * Positions are the reader's `data-p` span indexes, so this is the server-side
 * equivalent of the reader asking for the same range - which is why the AI
 * review summary derives its passage here rather than trusting the client to
 * say what the text at a position is.
 *
 * @param {string} html
 * @param {number} fromPosition
 * @param {number} toPosition - inclusive
 * @returns {string}
 */
export const extractPassage = (html, fromPosition, toPosition) => {
  const spanRegexp = new RegExp("<span data-p=([0-9]+)>(.*?)</span>", "g");
  const parts = [];
  let previousSpanEnd;
  let match;

  while ((match = spanRegexp.exec(html)) !== null) {
    const position = parseInt(match[1], 10);
    if (position < fromPosition) continue;
    if (position > toPosition) break;

    if (previousSpanEnd !== undefined) {
      // The punctuation and paragraph breaks between two words
      parts.push(htmlToPlainText(html.substring(previousSpanEnd, match.index)));
    }
    parts.push(htmlToPlainText(match[2]));
    previousSpanEnd = spanRegexp.lastIndex;
  }

  return parts.join("").trim();
};

/**
 * @param {string} html
 *
 * @returns {string[]}
 */
export const htmlToWordList = (html) => {
  const wordRegexp = new RegExp("<span data-p=([0-9]+)?>(.*?)</span>", "g");
  const words = [];
  let match;
  while ((match = wordRegexp.exec(html)) !== null) {
    console.assert(words.length === parseInt(match[1], 10));
    words.push(match[2]);
  }
  return words;
};
